/**
 * PestEditorDrawer — entity-specific drawer для «Вредители» (AK-233).
 *
 * Секции (по wireframe-эталону B/C, адаптировано под схему reference.pests):
 *   1. Идентификация (название RU + латынь)
 *   2. Классификация (категория — select из pest_categories)
 *   3. Характеристики (отряд/семейство латынь, поколений в год, зимующая
 *      стадия, поражаемые части, ЭПВ)
 *
 * Только поля, существующие в схеме reference.pests (migration 002 + 047).
 * EN-перевод отложен (RU-таб). Вредители НЕ имеют колонки code.
 */
import { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  EditorSection,
  ReferenceCatalogDrawer,
} from '@/components/reference/ReferenceCatalogDrawer';
import { NameTranslationsField } from '@/components/reference/NameTranslationsField';
import { StringTagsField } from '@/components/reference/StringTagsField';
import { MultiSelectChips } from '@/components/reference/MultiSelectChips';
import { AffectedCropsField } from '@/components/reference/AffectedCropsField';
import { PhotoBank } from '@/components/reference/PhotoBank';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';
import { usePest } from '@/pages/platform-admin/reference/hooks/useCatalogQueries';
import type {
  AffectedCrop,
  NameTranslations,
  PestDetail,
  PestListItem,
  PestPayload,
  ReferenceCategory,
} from '@/pages/platform-admin/reference/types';

const AFFECTED_PART_OPTIONS = [
  { value: 'leaves', label: 'Листья' },
  { value: 'stem', label: 'Стебель' },
  { value: 'roots', label: 'Корни' },
  { value: 'ear', label: 'Колос' },
  { value: 'grain', label: 'Зерно' },
  { value: 'fruit', label: 'Плоды' },
  { value: 'whole_plant', label: 'Всё растение' },
];

const DAMAGE_TYPE_OPTIONS = [
  { value: 'chewing', label: 'Грызущий' },
  { value: 'sucking', label: 'Сосущий' },
  { value: 'mining', label: 'Минирующий' },
  { value: 'boring', label: 'Стеблевой/буравящий' },
  { value: 'root', label: 'Корневой' },
];

interface FormState {
  name_translations: NameTranslations;
  latin_name: string;
  category_id: string;
  order_latin: string;
  family_latin: string;
  generations_per_year: string;
  overwintering_stage: string;
  damage_type: string[];
  affected_parts: string[];
  economic_threshold_value: string;
  economic_threshold_unit: string;
  also_known_as: string[];
}

const EMPTY_FORM: FormState = {
  name_translations: { ru: '' },
  latin_name: '',
  category_id: '',
  order_latin: '',
  family_latin: '',
  generations_per_year: '',
  overwintering_stage: '',
  damage_type: [],
  affected_parts: [],
  economic_threshold_value: '',
  economic_threshold_unit: '',
  also_known_as: [],
};

/**
 * Build form from the FULL detail row (PR #290 B1). Previously took a
 * PestListItem and hardcoded overwintering_stage / damage_type / affected_parts
 * / ЭПВ to empty, which the full-replace PATCH then wiped — silent data loss.
 */
function pestToForm(p: PestDetail): FormState {
  return {
    name_translations: { ...p.name_translations },
    latin_name: p.latin_name ?? '',
    category_id: p.category_id ?? '',
    order_latin: p.order_latin ?? '',
    family_latin: p.family_latin ?? '',
    generations_per_year:
      p.generations_per_year == null ? '' : String(p.generations_per_year),
    overwintering_stage: p.overwintering_stage ?? '',
    damage_type: [...(p.damage_type ?? [])],
    affected_parts: [...(p.affected_parts ?? [])],
    economic_threshold_value:
      p.economic_threshold_value == null
        ? ''
        : String(p.economic_threshold_value),
    economic_threshold_unit: p.economic_threshold_unit ?? '',
    also_known_as: [...(p.also_known_as ?? [])],
  };
}

interface FormErrors {
  name?: string;
  category?: string;
}

function validate(f: FormState): FormErrors {
  const errs: FormErrors = {};
  const hasName = Object.values(f.name_translations).some((v) => v.trim().length > 0);
  if (!hasName) errs.name = 'Требуется название';
  if (!f.category_id) errs.category = 'Выберите категорию';
  return errs;
}

const numOrNull = (v: string): number | null => (v.trim() ? Number(v) : null);
const intOrNull = (v: string): number | null =>
  v.trim() ? Math.trunc(Number(v)) : null;

function toPayload(f: FormState, categories: ReferenceCategory[]): PestPayload {
  const trimmed: NameTranslations = {};
  for (const [k, v] of Object.entries(f.name_translations)) {
    if (v.trim()) trimmed[k] = v.trim();
  }
  const cat = categories.find((c) => c.id === f.category_id);
  return {
    name_translations: trimmed,
    latin_name: f.latin_name.trim() || null,
    common_code: null,
    category: cat?.code ?? 'unknown',
    category_id: f.category_id || null,
    order_latin: f.order_latin.trim() || null,
    family_latin: f.family_latin.trim() || null,
    generations_per_year: intOrNull(f.generations_per_year),
    overwintering_stage: f.overwintering_stage.trim() || null,
    damage_type: f.damage_type,
    affected_parts: f.affected_parts,
    economic_threshold_value: numOrNull(f.economic_threshold_value),
    economic_threshold_unit: f.economic_threshold_unit.trim() || null,
    also_known_as: f.also_known_as,
  };
}

export interface PestEditorDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  pest: PestListItem | null;
  categories: ReferenceCategory[];
  onSubmit: (payload: PestPayload) => Promise<void>;
}

export function PestEditorDrawer({
  open,
  onOpenChange,
  mode,
  pest,
  categories,
  onSubmit,
}: PestEditorDrawerProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  // AK-257 — affected crops feed the PhotoBank lenses.
  const [affectedCrops, setAffectedCrops] = useState<AffectedCrop[]>([]);

  // PR #290 B1 — load full detail in edit mode (see usePest / pestToForm).
  const editId = open && mode === 'edit' && pest ? pest.id : null;
  const detailQ = usePest(editId);
  const detailLoading = Boolean(editId) && detailQ.isLoading;

  useEffect(() => {
    if (!open) return;
    if (mode === 'create') {
      setForm(EMPTY_FORM);
      setTouched(false);
      return;
    }
    if (detailQ.data) {
      setForm(pestToForm(detailQ.data));
      setTouched(false);
    }
  }, [open, mode, detailQ.data]);

  const errors = validate(form);
  const hasErrors = Object.keys(errors).length > 0;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setTouched(true);
    if (hasErrors) return;
    setSaving(true);
    try {
      await onSubmit(toPayload(form, categories));
      onOpenChange(false);
    } catch (err) {
      // AK-574 — отказ сервера виден пользователю: drawer остаётся открытым,
      // текст 409/422 в тосте. Раньше промах уходил в unhandled rejection.
      toast.error(describeApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const show = (err?: string) => (touched && err ? err : undefined);

  return (
    <ReferenceCatalogDrawer
      open={open}
      onOpenChange={onOpenChange}
      mode={mode}
      entityLabel="вредителя"
      headerSlot={
        form.name_translations.ru ? (
          <span className="text-sm font-medium text-foreground">
            {form.name_translations.ru}
          </span>
        ) : undefined
      }
      saveDisabled={detailLoading || (touched && hasErrors)}
      saveLoading={saving}
      onSave={handleSave}
    >
      {detailLoading ? (
        <div className="space-y-4 p-1">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <>
      <EditorSection number={1} title="Идентификация">
        <Label className="mb-1.5 block text-sm">Латинское название</Label>
        <Input
          value={form.latin_name}
          onChange={(e) => set('latin_name', e.target.value)}
          placeholder="Eurygaster integriceps"
          className="mb-4 italic"
        />
        <Label className="mb-1.5 block text-sm">
          Название (RU) <span className="text-destructive">*</span>
        </Label>
        <NameTranslationsField
          value={form.name_translations}
          onChange={(v) => set('name_translations', v)}
          locales={['ru']}
          hint="EN-перевод отложен (отдельный тикет). Сейчас ведём только RU."
          hasError={Boolean(show(errors.name))}
        />
        <Label className="mb-1.5 mt-4 block text-sm">
          Другие названия (народные/местные)
        </Label>
        <StringTagsField
          values={form.also_known_as}
          onChange={(v) => set('also_known_as', v)}
          placeholder="напр. «черепашка» (Enter добавит)"
          removeLabel="Убрать синоним"
        />
        <p className="mt-1.5 text-xs text-muted-foreground">
          Народные и местные синонимы. По ним ищут в каталоге и подбирают в
          реестре СЗР наравне с основным именем.
        </p>
      </EditorSection>

      <EditorSection number={2} title="Классификация" hint="из pest_categories">
        <Label className="mb-1.5 block text-sm">
          Категория <span className="text-destructive">*</span>
        </Label>
        <Select
          value={form.category_id || undefined}
          onValueChange={(v) => set('category_id', v)}
        >
          <SelectTrigger
            className={show(errors.category) ? 'border-destructive' : undefined}
          >
            <SelectValue placeholder="Выберите категорию…" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name_translations.ru ?? c.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {show(errors.category) && (
          <p className="mt-1.5 flex items-center gap-1 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5" />
            {errors.category}
          </p>
        )}
        <p className="mt-1.5 text-xs text-muted-foreground">
          Управляется в разделе «Категории вредителей».
        </p>
      </EditorSection>

      <EditorSection number={3} title="Характеристики">
        <div className="mb-4 grid grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block text-sm">Отряд (латынь)</Label>
            <Input
              value={form.order_latin}
              onChange={(e) => set('order_latin', e.target.value)}
              placeholder="Hemiptera"
              className="italic"
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-sm">Семейство (латынь)</Label>
            <Input
              value={form.family_latin}
              onChange={(e) => set('family_latin', e.target.value)}
              placeholder="Scutelleridae"
              className="italic"
            />
          </div>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block text-sm">Поколений в год</Label>
            <Input
              type="number"
              value={form.generations_per_year}
              onChange={(e) => set('generations_per_year', e.target.value)}
              placeholder="1"
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-sm">Зимующая стадия</Label>
            <Input
              value={form.overwintering_stage}
              onChange={(e) => set('overwintering_stage', e.target.value)}
              placeholder="Имаго"
            />
          </div>
        </div>

        <Label className="mb-1.5 block text-sm">Тип повреждения</Label>
        <MultiSelectChips
          options={DAMAGE_TYPE_OPTIONS}
          selected={form.damage_type}
          onChange={(v) => set('damage_type', v)}
          placeholder="Выберите типы повреждения…"
        />

        <Label className="mb-1.5 mt-4 block text-sm">Поражаемые части</Label>
        <MultiSelectChips
          options={AFFECTED_PART_OPTIONS}
          selected={form.affected_parts}
          onChange={(v) => set('affected_parts', v)}
          placeholder="Выберите части растения…"
        />

        <Label className="mb-1.5 mt-4 block text-sm">
          Экономический порог вредоносности (ЭПВ)
        </Label>
        <div className="grid grid-cols-2 gap-4">
          <Input
            type="number"
            value={form.economic_threshold_value}
            onChange={(e) => set('economic_threshold_value', e.target.value)}
            placeholder="значение"
          />
          <Input
            value={form.economic_threshold_unit}
            onChange={(e) => set('economic_threshold_unit', e.target.value)}
            placeholder="экз./м²"
          />
        </div>
      </EditorSection>

      {/* AK-257 — section 4: affected crops (write-path, Q2 two-step). */}
      <EditorSection
        number={4}
        title="Поражаемые культуры"
        hint="кого поражает вредитель"
      >
        <AffectedCropsField
          dict="pests"
          entityId={editId}
          onCropsChange={setAffectedCrops}
        />
      </EditorSection>

      {/* AK-257 — section 5: photobank. Saved entity + ≥1 affected crop only. */}
      {editId && affectedCrops.length > 0 && (
        <EditorSection number={5} title="Фотобанк" hint="фото по культурам">
          <PhotoBank dict="pests" entityId={editId} affectedCrops={affectedCrops} />
        </EditorSection>
      )}
        </>
      )}
    </ReferenceCatalogDrawer>
  );
}

export default PestEditorDrawer;
